import { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Flex,
  IconButton,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Spinner,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { FaBell, FaClock, FaFileContract, FaMoneyCheckAlt, FaShip } from "react-icons/fa";
import { DateTime } from "luxon";
import { useNavigate } from "react-router-dom";
import { crm } from "../theme";
import { AppNotification, NotificationList } from "../models/Notification";
import {
  getMyNotifications,
  markAllNotificationsRead,
  markNotificationRead,
} from "../services/notificationService";

const POLL_MS = 60_000;

const kindIcon: Record<string, JSX.Element> = {
  statusLimit: <FaClock />,
  duimp: <FaFileContract />,
  shipsgo: <FaShip />,
  swift: <FaMoneyCheckAlt />,
};

/** Sino do canto superior direito: contador de não lidas + popover com as últimas notificações. */
const NotificationBell = () => {
  const [data, setData] = useState<NotificationList>({ unreadCount: 0, items: [] });
  const [loading, setLoading] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      setData(await getMyNotifications());
    } catch {
      // sino é acessório: falha de rede não interrompe a tela
    }
  }, []);

  useEffect(() => {
    load();
    const timer = setInterval(() => {
      if (!document.hidden) load();
    }, POLL_MS);
    const onVisible = () => {
      if (!document.hidden) load();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [load]);

  const open = async () => {
    onOpen();
    setLoading(true);
    await load();
    setLoading(false);
  };

  const openItem = async (item: AppNotification) => {
    if (!item.readAt) {
      setData((d) => ({
        unreadCount: Math.max(0, d.unreadCount - 1),
        items: d.items.map((i) => (i.id === item.id ? { ...i, readAt: DateTime.now().toISO() } : i)),
      }));
      markNotificationRead(item.id).catch(() => undefined);
    }
    // Swift pendente: abre a lista de swifts, onde fica o "Marcar enviado"
    if (item.kind === "swift") {
      onClose();
      navigate("/gestao/swifts");
    } else if (item.processId) {
      onClose();
      navigate(`/process?id=${item.processId}`);
    }
  };

  const readAll = async () => {
    setData((d) => ({ unreadCount: 0, items: d.items.map((i) => ({ ...i, readAt: i.readAt ?? DateTime.now().toISO() })) }));
    await markAllNotificationsRead().catch(() => undefined);
  };

  return (
    <Popover isOpen={isOpen} onOpen={open} onClose={onClose} placement="bottom-end" isLazy>
      <PopoverTrigger>
        <Box position="relative">
          <IconButton
            aria-label={data.unreadCount ? `${data.unreadCount} notificações não lidas` : "Notificações"}
            icon={<FaBell />}
            variant="ghost"
            color={crm.text}
            size="md"
          />
          {data.unreadCount > 0 && (
            <Flex
              position="absolute"
              top="2px"
              right="2px"
              minW="18px"
              h="18px"
              px="4px"
              borderRadius="full"
              bg={crm.danger}
              color="white"
              fontSize="11px"
              fontWeight="700"
              align="center"
              justify="center"
              pointerEvents="none"
            >
              {data.unreadCount > 99 ? "99+" : data.unreadCount}
            </Flex>
          )}
        </Box>
      </PopoverTrigger>
      <PopoverContent w={{ base: "92vw", md: "380px" }} borderRadius="12px" boxShadow={crm.shadowLg}>
        <PopoverHeader borderBottom={`1px solid ${crm.border}`}>
          <Flex align="center" justify="space-between">
            <Text fontWeight="700">Notificações</Text>
            {data.unreadCount > 0 && (
              <Button size="xs" variant="ghost" color={crm.brand} onClick={readAll}>
                Marcar todas como lidas
              </Button>
            )}
          </Flex>
        </PopoverHeader>
        <PopoverBody p="0" maxH="420px" overflowY="auto">
          {loading && data.items.length === 0 ? (
            <Flex justify="center" py="24px">
              <Spinner size="sm" />
            </Flex>
          ) : data.items.length === 0 ? (
            <Text color={crm.textMuted} fontSize="sm" textAlign="center" py="24px">
              Nenhuma notificação.
            </Text>
          ) : (
            data.items.map((item) => (
              <Flex
                key={item.id}
                gap="10px"
                px="14px"
                py="10px"
                borderBottom={`1px solid ${crm.border}`}
                bg={item.readAt ? undefined : crm.brandSoft}
                cursor={item.processId || !item.readAt ? "pointer" : "default"}
                _hover={{ bg: crm.subtle }}
                onClick={() => openItem(item)}
              >
                <Box color={crm.brand} pt="3px">
                  {kindIcon[item.kind] ?? <FaBell />}
                </Box>
                <Box minW={0} flex="1">
                  <Text fontSize="sm" fontWeight={item.readAt ? "500" : "700"} color={crm.text}>
                    {item.title}
                  </Text>
                  {item.message && (
                    <Text fontSize="xs" color={crm.textMuted} mt="2px" overflowWrap="anywhere">
                      {item.message}
                    </Text>
                  )}
                  {item.createdDate && (
                    <Text fontSize="11px" color={crm.textFaint} mt="4px">
                      {DateTime.fromISO(item.createdDate).toFormat("dd/LL/yyyy HH:mm")}
                    </Text>
                  )}
                </Box>
              </Flex>
            ))
          )}
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};

export default NotificationBell;
